// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseToolbar, Loading } from '~/components/common';
import { ActivityDetailModal, BaseTimeLine } from '~/components/module/activity';
// Others
import { LoadingSizeEnum, TypeJobDetailEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IActivity } from '~/utils/interface/activity';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
import { IBaseResponse, IListDataResponse } from '~/utils/interface/base';
import { IParamQueryJobDetail } from '~/utils/interface/jobs';
import { getDetailJob } from '~/thunks/job/jobThunk';
// Styles, images, icons
import styles from './ActivityJobTab.module.scss';
import { Box, Stack, Typography } from '@mui/material';

type Props = {};

const cx = classNames.bind(styles);

const ActivityJobTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activityList, setActivityList] = useState<IActivity[]>([]);
  const [currentPageActivity, setCurrentPageActivity] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [hasMoreActivity, setHasMoreActivity] = useState<boolean>(true);
  const [activitySelected, setActivitySelected] = useState<IActivity | null>(null);
  const [isShowActivityDetail, setIsShowActivityDetail] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!jobId) return;

    const params: IParamQueryJobDetail = {
      jobId: jobId,
      type: TypeJobDetailEnum.ACTIVITY,
      query: {
        page: currentPageActivity,
        limit: DEFAULT_LIMIT_PAGE,
      },
    };

    handleGetListActivityJob(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageActivity]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListActivityJob = (params: IParamQueryJobDetail) => {
    if (currentPageActivity === DEFAULT_CURRENT_PAGE) {
      setIsLoading(true);
    }

    dispatch(getDetailJob(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IListDataResponse<IActivity[]>>;

        if (!data) return;

        const { responses } = data;

        if (!responses || responses?.length === 0) {
          setHasMoreActivity(false);
          return;
        }

        setActivityList((prev) => {
          const newData = responses.filter((data) => !prev.some((old) => old.id === data.id));
          return [...prev, ...newData];
        });

        setHasMoreActivity(true);
      })
      .catch((_error) => {
        setHasMoreActivity(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleLoadMore = () => {
    setCurrentPageActivity((prev) => prev + 1);
  };

  const handleViewDetail = (activity: IActivity) => {
    setIsShowActivityDetail(true);
    setActivitySelected(activity);
  };

  const handleCloseActivityDetail = () => {
    setIsShowActivityDetail(false);
    setActivitySelected(null);
  };
  //#endregion Handle Function

  return (
    <Box id='taskJobDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <BaseToolbar title={t('job_activity_title')} />
      </Stack>

      {!isLoading &&
        (activityList?.length > 0 ? (
          <BaseTimeLine
            data={activityList}
            onLoadMore={handleLoadMore}
            hasMore={hasMoreActivity}
            onView={handleViewDetail}
            loadMoreSize={LoadingSizeEnum.MEDIUM}
          />
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        ))}

      <ActivityDetailModal
        isOpen={isShowActivityDetail}
        {...(activitySelected && { activity: activitySelected })}
        onClose={handleCloseActivityDetail}
      />

      {isLoading && <Loading />}
    </Box>
  );
};

export default ActivityJobTab;
